import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { Theme } from '../ui/theme';
import { GameState } from '../systems/GameState';
import { QuestEngine } from '../systems/QuestEngine';
import { QUESTS, DAILY_POOL, WEEKLY_POOL } from '../data/quests';
import { Button } from '../ui/Button';
import { ToastManager } from '../ui/ToastManager';
import type { Quest } from '../types';

export class QuestPanelScene extends Phaser.Scene {
  private toast!: ToastManager;
  private listContainer: Phaser.GameObjects.Container | null = null;
  private filter: 'daily' | 'weekly' | 'achievement' = 'daily';

  constructor() { super({ key: 'QuestPanelScene', active: false }); }

  create(): void {
    this.toast = new ToastManager(this);
    QuestEngine.syncFromStats();

    // 어두운 배경
    this.add.rectangle(GAME_WIDTH/2, GAME_HEIGHT/2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6);
    // 패널 with shadow
    const panelG = this.add.graphics();
    panelG.fillStyle(Theme.shadow.color, 0.1);
    panelG.fillRoundedRect(103, 52, GAME_WIDTH - 204, GAME_HEIGHT - 102, 20);
    panelG.fillStyle(0xfff5f8, 1);
    panelG.fillRoundedRect(100, 48, GAME_WIDTH - 200, GAME_HEIGHT - 100, 20);
    panelG.lineStyle(2, Theme.colors.pink.light, 1);
    panelG.strokeRoundedRect(100, 48, GAME_WIDTH - 200, GAME_HEIGHT - 100, 20);

    this.add.text(GAME_WIDTH/2, 100, '🎯 퀘스트', { fontFamily: Theme.font.family, fontSize: '32px', fontStyle: '800', color: Theme.hex.text }).setOrigin(0.5);

    // 탭
    ['daily','weekly','achievement'].forEach((tab, i) => {
      const x = 380 + i * 240;
      const active = this.filter === tab;
      const tabG = this.add.graphics();
      tabG.fillStyle(active ? Theme.colors.pink.main : Theme.colors.pink.bg, 1);
      tabG.fillRoundedRect(x - 110, 136, 220, 48, 12);
      tabG.lineStyle(1.5, active ? Theme.colors.pink.dark : Theme.colors.pink.light, 1);
      tabG.strokeRoundedRect(x - 110, 136, 220, 48, 12);
      const hitR = this.add.rectangle(x, 160, 220, 48, 0, 0);
      hitR.setInteractive({ useHandCursor: true });
      hitR.on('pointerdown', () => { this.filter = tab as typeof this.filter; this.renderList(); });
      this.add.text(x, 160, tab === 'daily' ? '📅 일일' : tab === 'weekly' ? '📆 주간' : '🏆 업적', { fontFamily: Theme.font.family, fontSize: '17px', fontStyle: '800', color: active ? '#fff' : Theme.hex.text }).setOrigin(0.5);
    });

    this.renderList();

    new Button(this, GAME_WIDTH/2, GAME_HEIGHT - 80, '닫기', () => this.scene.stop(), { width: 160, height: 48, style: 'secondary' });
  }

  private renderList(): void {
    if (this.listContainer) this.listContainer.destroy();
    this.listContainer = this.add.container(0,0);

    let quests: Quest[] = [];
    if (this.filter === 'daily') quests = DAILY_POOL.filter(q => GameState.data.questProgress.some(p => p.questId === q.id)) as Quest[];
    else if (this.filter === 'weekly') quests = WEEKLY_POOL.filter(q => GameState.data.questProgress.some(p => p.questId === q.id)) as Quest[];
    else quests = QUESTS;

    if (quests.length === 0) {
      const t = this.add.text(GAME_WIDTH/2, 280, '진행 중인 퀘스트가 없어요!', { fontFamily: 'Pretendard Variable', fontSize: '18px', color: '#7a5b80' }).setOrigin(0.5);
      this.listContainer.add(t);
      // refresh button for daily/weekly
      if (this.filter === 'daily' || this.filter === 'weekly') {
        const btn = new Button(this, GAME_WIDTH/2, 360, '🎲 일일 새로고침', () => {
          if (this.filter === 'daily') QuestEngine.refreshDailyQuests();
          else QuestEngine.refreshWeeklyQuests();
          this.renderList();
        }, { width: 240, height: 48, style: 'gold' });
        this.listContainer.add(btn);
      }
      return;
    }

    quests.slice(0, 8).forEach((q, i) => {
      const y = 230 + i * 90;
      const prog = GameState.data.questProgress.find(p => p.questId === q.id);
      const cur = prog?.current ?? 0;
      const claimed = prog?.claimed ?? false;
      const complete = cur >= q.goal.target;
      const rowG = this.add.graphics();
      rowG.fillStyle(claimed ? 0xeeeeee : complete ? Theme.colors.gold.light : 0xffffff, 1);
      rowG.fillRoundedRect(140, y - 40, GAME_WIDTH - 284, 80, 12);
      rowG.lineStyle(2, complete ? Theme.colors.gold.main : Theme.colors.pink.light, 1);
      rowG.strokeRoundedRect(140, y - 40, GAME_WIDTH - 284, 80, 12);
      this.listContainer!.add(rowG);
      const titleText = this.add.text(168, y - 18, q.title, { fontFamily: Theme.font.family, fontSize: '16px', fontStyle: '800', color: Theme.hex.text });
      const progressText = this.add.text(168, y + 10, `${cur} / ${q.goal.target}${claimed ? ' (받음)' : complete ? ' ✨' : ''}`, { fontFamily: Theme.font.family, fontSize: '13px', color: Theme.hex.textMid });
      const rewardText = this.add.text(GAME_WIDTH - 320, y, `⭐ ${q.reward.stars ?? 0} 💰 ${q.reward.coins ?? 0}${q.reward.outfitId ? ' 👗' : ''}`, { fontFamily: Theme.font.family, fontSize: '14px', fontStyle: '700', color: Theme.hex.pinkDark }).setOrigin(0, 0.5);
      this.listContainer!.add([titleText, progressText, rewardText]);

      if (complete && !claimed) {
        const claimBtn = new Button(this, GAME_WIDTH - 220, y, '✨ 받기', () => {
          if (QuestEngine.claim(q.id)) { this.toast.show('🎉 보상!', 'gold'); this.renderList(); }
        }, { width: 110, height: 40, style: 'gold' });
        this.listContainer!.add(claimBtn);
      }
    });
  }
}
